<template>
  <div class="">
    <section-title content="Información del Especialista" :has-line="true" />
    <div class="px-4">
      <validation-observer ref="generalInfoForm" tag="div">
        <div class="row p-1 mb-4">
          <div class="col-sm-3 my-2">
            <label class="data-title mb-0">Nombre:</label>
            <validation-provider
              v-slot="{ errors }"
              name="specialistName"
              rules="required"
            >
              <custom-input
                v-model="model.firstName"
                :custom-error-msg="'El nombre es requerido.'"
                :error="errors[0]"
              />
            </validation-provider>
          </div>
          <div class="col-sm-3 my-2">
            <label class="data-title mb-0">Segundo Nombre:</label>
            <custom-input v-model="model.middleName" />
          </div>
          <div class="col-sm-3 my-2">
            <label class="data-title mb-0">Apellido Paterno:</label>
            <validation-provider
              v-slot="{ errors }"
              name="specialistLastPaternalName"
              rules="required"
            >
              <custom-input
                v-model="model.lastPaternalName"
                :custom-error-msg="'El apellido paterno es requerido.'"
                :error="errors[0]"
              />
            </validation-provider>
          </div>
          <div class="col-sm-3 my-2">
            <label class="data-title mb-0">Apellido Materno:</label>
            <custom-input v-model="model.lastMaternalName" />
          </div>
          <div class="col-sm-3 my-2">
            <label class="data-title mb-0">Correo Electrónico:</label>
            <validation-provider
              v-slot="{ errors, failedRules }"
              name="specialistEmail"
              rules="required|email"
            >
              <custom-input
                v-model="model.email"
                :custom-error-msg="
                  failedRules['email']
                    ? 'El correo electrónico no es válido.'
                    : 'El correo electrónico es requerido.'
                "
                :error="errors[0]"
                type="email"
              />
            </validation-provider>
          </div>
          <div v-if="isHealthDeptNoEnabled" class="col-sm-3 my-2">
            <label class="data-title mb-0">SSN:</label>
            <custom-input :disabled="true" :value="maskSSN(model.ssn)" />
          </div>
          <div v-if="isHealthDeptNoEnabled && !isNew" class="col-sm-3 my-2">
            <label class="data-title mb-0">Número de Registro de Salud:</label>
            <validation-provider v-slot="{ errors }" rules="max:16">
              <custom-input
                v-model="model.registrationNumber"
                :custom-error-msg="'El campo no debe ser mayor a 16 caracteres'"
                :error="errors[0]"
                error-class="position-absolute"
              />
            </validation-provider>
          </div>
          <div
            v-if="
              isHealthDeptNoEnabled && isMedicaidDocumentationWorkflowEnabled
            "
            class="col-sm-3 my-2"
          >
            <label class="data-title mb-0">NPI Especialista:</label>
            <validation-provider v-slot="{ errors }" rules="max:10">
              <custom-input
                v-model="model.nationalIdentifier"
                :custom-error-msg="'El campo no debe ser mayor a 10 caracteres'"
                :error="errors[0]"
                error-class="position-absolute"
                maxlength="10"
              />
            </validation-provider>
          </div>
          <div
            v-if="
              isHealthDeptNoEnabled && isMedicaidDocumentationWorkflowEnabled
            "
            class="col-sm-3 my-2"
          >
            <label class="data-title mb-0">
              Fecha de Vencimiento Medicaid:
            </label>
            <custom-date-picker
              v-model="model.medicaidDueDate"
              :class-container="'w-100'"
              format="dd/MMM./yyyy"
              :min-date="new Date()"
            />
          </div>
          <div v-if="isReadOnly && canUpdateInformation" class="col-sm-3 my-2">
            <action-button
              class="custom-mt-action custom-action-sm"
              i="fas fa-redo fa-fw"
              variant="success close-border"
              @click="updateSpecialistInfo"
            >
              Actualizar
            </action-button>
          </div>
        </div>
      </validation-observer>
    </div>
    <section-title content="Información de Dirección" :has-line="true" />
    <div class="px-4">
      <validation-observer tag="div">
        <div class="row p-1 mb-4">
          <div class="col-sm-4 my-2">
            <label class="data-title mb-0">Dirección - Línea 1:</label>
            <custom-input v-model="model.postalAddressLine1" />
          </div>
          <div class="col-sm-4 my-2">
            <label class="data-title mb-0">Municipio Postal:</label>
            <custom-drop-down
              v-model="selectedMunicipality"
              :clearable="false"
              fieldkey="id"
              fieldname="name"
              :options="municipalityOptions"
              placeholder="Seleccione el municipio"
            />
          </div>
          <div class="col-sm-4 my-2">
            <label class="data-title mb-0">Código Postal:</label>
            <validation-provider
              v-slot="{ errors }"
              mode="eager"
              name="postalZipCode"
              rules="max:5"
            >
              <custom-input
                v-model="model.postalZipCode"
                :error="errors[0]"
                mask-type="ZipcodeExt"
                maxlength="5"
              />
            </validation-provider>
          </div>
          <div class="col-sm-4 my-2">
            <label class="data-title mb-0">Dirección - Línea 2:</label>
            <custom-input v-model="model.postalAddressLine2" />
          </div>
          <div class="col-sm-4 my-2">
            <label class="data-title mb-0">Número de Teléfono Personal:</label>
            <validation-provider
              v-slot="{ errors }"
              name="personalPhone"
              rules="max:20"
            >
              <custom-input
                v-model="model.personalPhone"
                :error="errors[0]"
                :mask-type="'Phone'"
              />
            </validation-provider>
          </div>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomDatePicker from 'custom-date-picker';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import { getMunicipalityOptions as _getMunicipalityOptions } from '@/services/provider/specialist.api.js';
export default {
  name: 'SpecialistGeneralInfo',
  components: {
    CustomInput,
    CustomDropDown,
    SectionTitle,
    ActionButton,
    ValidationProvider,
    ValidationObserver,
    CustomDatePicker,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    isHealthDeptNoEnabled: {
      type: Boolean,
      default: false,
    },
    isMedicaidDocumentationWorkflowEnabled: {
      type: Boolean,
      default: false,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      municipalityOptions: [],
      selectedMunicipality: null,
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        const result = { ...val };
        this.$emit('input', result);
      },
    },
    canUpdateInformation() {
      return this.userHasPermissions('rempro.specialist.updateinformation');
    },
  },
  methods: {
    async getMunicipalityOptions() {
      const { data } = await _getMunicipalityOptions();
      if (data) {
        this.municipalityOptions = data;
        if (
          this.model &&
          this.model.postalMunicipalityId &&
          this.municipalityOptions.length
        ) {
          this.selectedMunicipality = this.municipalityOptions.find(
            (x) => x.id == this.model.postalMunicipalityId
          );
        }
      }
    },
    clearSelectedMunicipality() {
      this.selectedMunicipality = null;
      this.model.postalMunicipalityId = null;
    },
    maskSSN(ssn) {
      if (!ssn) return '';

      let cleanSSN = ssn.trim().replace(/\D/g, '');
      if (!/^\d{9}$/.test(cleanSSN)) {
        return ssn;
      }
      let last4 = cleanSSN.slice(-4);
      return `***-**-${last4}`;
    },
    updateSpecialistInfo() {
      this.$emit('update');
    },
    async validateForm() {
      return await this.$refs.generalInfoForm.validate();
    },
  },
  async mounted() {
    await this.getMunicipalityOptions();
    if (
      this.model.postalMunicipalityId &&
      this.municipalityOptions.length > 0
    ) {
      this.selectedMunicipality = this.municipalityOptions.find(
        (x) => x.id == this.model.postalMunicipalityId
      );
    }
  },
  watch: {
    selectedMunicipality(newVal) {
      this.model.postalMunicipalityId = newVal ? newVal.id : null;
    },
    'model.postalMunicipalityId': function(newVal) {
      if (newVal == null) {
        this.selectedMunicipality = null;
        return;
      }
      if (this.municipalityOptions && this.municipalityOptions.length) {
        this.selectedMunicipality = this.municipalityOptions.find(
          (x) => x.id == newVal
        );
      }
    },
  },
};
</script>
<style scoped lang="css">
.custom-mt-action {
  margin-top: 1.8rem !important;
}

.custom-action-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 0.8rem !important;
  font-size: 0.95rem !important;
  min-width: 350px;
}
::v-deep .custom-datetime-picker-container .fa-calendar-day {
  font-weight: 400;
}
</style>
